import React, { useState, useEffect } from "react";
import { useLocaleNavigate } from "../lib/i18n";
import { useActiveLocale } from "../lib/i18n";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { api } from "../lib/api";
import { toast } from "sonner";
import { User, Globe, Lock, ShieldAlert, Mail, AlertTriangle, GitCompareArrows, History, Trash2, BellOff } from "lucide-react";

export default function Account() {
  const { user, refresh, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useLocaleNavigate();
  const activeLocale = useActiveLocale();

  // Profile form
  const [name, setName] = useState("");
  const [locale, setLocale] = useState("de");
  const [savingProfile, setSavingProfile] = useState(false);
  // Phase 53 — Boost-reminder email opt-out
  const [emailReminderOff, setEmailReminderOff] = useState(false);
  const [savingNotif, setSavingNotif] = useState(false);

  // Email change form
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  // Password form
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdError, setPwdError] = useState("");

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Compare-History
  const [compareHistory, setCompareHistory] = useState([]);
  const [compareLoading, setCompareLoading] = useState(false);

  useEffect(() => {
    if (user && user.id) {
      setName(user.name || "");
      setLocale(user.preferred_locale || lang || "de");
      setEmailReminderOff(!!user.boost_reminder_email_disabled);
    }
  }, [user, lang]);

  useEffect(() => {
    if (!user || !user.id) return;
    setCompareLoading(true);
    api.compareHistory()
      .then((r) => setCompareHistory(Array.isArray(r?.items) ? r.items : []))
      .catch(() => setCompareHistory([]))
      .finally(() => setCompareLoading(false));
  }, [user]);

  const reopenCompare = (entry) => {
    const search = new URLSearchParams();
    entry.ids.forEach((x) => search.append("id", x));
    navigate(`/compare?${search.toString()}`);
  };

  const deleteCompareEntry = async (entryId) => {
    try {
      await api.compareHistoryDelete(entryId);
      setCompareHistory((p) => p.filter((e) => e.id !== entryId));
      toast.success("Eintrag entfernt");
    } catch {
      toast.error("Entfernen fehlgeschlagen");
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const patch = {};
      if (name && name !== user.name) patch.name = name.trim();
      if (locale && locale !== user.preferred_locale) patch.preferred_locale = locale;
      if (Object.keys(patch).length === 0) {
        toast.success(t.account.profileSaved);
      } else {
        await api.updateProfile(patch);
        if (patch.preferred_locale) setLang(patch.preferred_locale);
        toast.success(t.account.profileSaved);
        await refresh();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSavingProfile(false);
    }
  };

  // Phase 53 — Boost-Reminder Email toggle
  const toggleEmailReminder = async (next) => {
    setEmailReminderOff(next);
    setSavingNotif(true);
    try {
      await api.updateProfile({ boost_reminder_email_disabled: next });
      toast.success(next
        ? (lang === "en" ? "Boost-reminder emails disabled" : lang === "fr" ? "Emails de rappel désactivés" : lang === "it" ? "Email di promemoria disattivate" : "Boost-Erinnerungs-E-Mails deaktiviert")
        : (lang === "en" ? "Boost-reminder emails enabled" : lang === "fr" ? "Emails de rappel activés" : lang === "it" ? "Email di promemoria attivate" : "Boost-Erinnerungs-E-Mails aktiviert"));
      await refresh();
    } catch (err) {
      setEmailReminderOff(!next);  // revert on failure
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSavingNotif(false);
    }
  };

  const submitEmailChange = async (e) => {
    e.preventDefault();
    setEmailError("");
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || !trimmed.includes("@")) { setEmailError(t.auth.email); return; }
    if (trimmed === (user.email || "").toLowerCase()) { setEmailError(t.account.emailChangeSame); return; }
    if (!emailPwd) { setEmailError(t.account.currentPassword); return; }
    setEmailBusy(true);
    try {
      await api.requestEmailChange(trimmed, emailPwd, activeLocale || lang || "de");
      setEmailSent(true);
      setEmailPwd("");
      toast.success(t.account.emailChangeSent);
    } catch (err) {
      setEmailError(err?.response?.data?.detail || "Error");
    } finally {
      setEmailBusy(false);
    }
  };

  const closeEmailForm = () => {
    setEmailFormOpen(false);
    setEmailSent(false);
    setNewEmail("");
    setEmailPwd("");
    setEmailError("");
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (newPwd.length < 6) { setPwdError(`${t.auth.password}: min 6`); return; }
    if (newPwd !== confirmPwd) { setPwdError(t.account.pwMismatch); return; }
    setSavingPwd(true);
    try {
      await api.changePassword(curPwd, newPwd);
      toast.success(t.account.passwordChanged);
      setCurPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (err) {
      setPwdError(err?.response?.data?.detail || "Error");
    } finally {
      setSavingPwd(false);
    }
  };

  const submitDelete = async () => {
    setDeleteError("");
    if (!deletePwd) { setDeleteError(t.account.currentPassword); return; }
    if (deleteConfirm.trim().toUpperCase() !== t.account.deleteConfirmKeyword) {
      setDeleteError(t.account.deleteConfirmInput); return;
    }
    setDeleting(true);
    try {
      await api.deleteAccount(deletePwd, deleteConfirm.trim().toUpperCase());
      toast.success(t.account.deletedSuccess);
      // hard logout + redirect
      try { await logout(); } catch { /* ignore */ }
      setTimeout(() => navigate("/"), 700);
    } catch (err) {
      setDeleteError(err?.response?.data?.detail || "Error");
      setDeleting(false);
    }
  };

  if (!user || !user.id) return null;

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 lg:px-8" data-testid="account-page">
      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">CarHive</p>
      <h1 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">{t.account.title}</h1>
      <p className="mt-2 max-w-prose text-sm text-zinc-400">{t.account.subtitle}</p>

      {/* PROFILE */}
      <Card className="mt-8 border-zinc-800 bg-zinc-950 p-6 text-white sm:p-8" data-testid="account-profile-card">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-600/15 text-red-500"><User className="h-4 w-4" /></div>
          <div>
            <h2 className="text-lg font-semibold">{t.account.profileTitle}</h2>
            <p className="text-sm text-zinc-400">{t.account.profileSubtitle}</p>
          </div>
        </div>
        <form onSubmit={saveProfile} className="mt-6 space-y-5">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.account.emailLabel}</Label>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-zinc-800 bg-black px-3 py-2.5 text-sm text-zinc-300" data-testid="account-email-row">
              <span className="inline-flex items-center gap-2 break-all">
                <Mail className="h-3.5 w-3.5 shrink-0 text-zinc-500" /> {user.email}
              </span>
              {!emailFormOpen && (
                <Button
                  type="button"
                  onClick={() => setEmailFormOpen(true)}
                  variant="outline"
                  size="sm"
                  className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900"
                  data-testid="account-email-change-cta"
                >
                  {t.account.emailChangeCta}
                </Button>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-zinc-500">{t.account.emailNoteText}</p>

            {emailFormOpen && (
              <div className="mt-3 rounded-lg border border-zinc-800 bg-black/40 p-4" data-testid="account-email-change-form">
                {emailSent ? (
                  <div className="space-y-4" data-testid="account-email-change-sent">
                    <div className="flex items-start gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                      <Mail className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                      <p className="text-sm leading-relaxed text-zinc-200">{t.account.emailChangeSent}</p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button type="button" onClick={closeEmailForm} variant="outline" className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900" data-testid="account-email-change-close">
                        {t.account.emailChangeCancel}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={submitEmailChange} className="space-y-4" data-testid="account-email-change-fields">
                    <div>
                      <p className="text-sm font-semibold text-white">{t.account.emailChangeTitle}</p>
                      <p className="mt-1 text-xs text-zinc-400">{t.account.emailChangeIntro}</p>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.account.emailChangeNewLabel}</Label>
                      <Input
                        type="email"
                        required
                        autoComplete="email"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                        className="mt-2 border-zinc-800 bg-black text-white"
                        data-testid="account-email-change-new"
                        placeholder="dein.neuer.name@example.ch"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.account.currentPassword}</Label>
                      <Input
                        type="password"
                        required
                        autoComplete="current-password"
                        value={emailPwd}
                        onChange={(e) => setEmailPwd(e.target.value)}
                        className="mt-2 border-zinc-800 bg-black text-white"
                        data-testid="account-email-change-pwd"
                      />
                    </div>
                    {emailError && <p className="text-sm text-red-500" data-testid="account-email-change-error">{emailError}</p>}
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Button type="submit" disabled={emailBusy || !newEmail || !emailPwd} className="bg-red-600 text-white hover:bg-red-500" data-testid="account-email-change-submit">
                        {emailBusy ? "..." : t.account.emailChangeSubmit}
                      </Button>
                      <Button type="button" onClick={closeEmailForm} variant="outline" className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900" data-testid="account-email-change-cancel">
                        {t.account.emailChangeCancel}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.account.nameLabel}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className="mt-2 border-zinc-800 bg-black text-white"
              data-testid="account-name-input"
              required
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">
              <span className="inline-flex items-center gap-1.5"><Globe className="h-3 w-3" />{t.account.langLabel}</span>
            </Label>
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value)}
              className="mt-2 w-full rounded-md border border-zinc-800 bg-black px-3 py-2.5 text-base text-white focus:border-red-600 focus:outline-none focus:ring-1 focus:ring-red-600/40"
              data-testid="account-locale-select"
            >
              <option value="de">{t.account.langDe}</option>
              <option value="fr">{t.account.langFr}</option>
              <option value="it">{t.account.langIt}</option>
              <option value="en">{t.account.langEn}</option>
            </select>
          </div>
          <Button type="submit" disabled={savingProfile} className="w-full bg-red-600 text-white hover:bg-red-500 sm:w-auto" data-testid="account-save-profile">
            {savingProfile ? "..." : t.account.saveProfile}
          </Button>
        </form>
      </Card>

      {/* PHASE 53 — NOTIFICATIONS (Boost-Reminder Email opt-out) */}
      <Card className="mt-6 border-zinc-800 bg-zinc-950 p-6 text-white sm:p-8" data-testid="account-notifications-card">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-600/15 text-red-500">
            <BellOff className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              {lang === "en" ? "Notifications" : lang === "fr" ? "Notifications" : lang === "it" ? "Notifiche" : "Benachrichtigungen"}
            </h2>
            <p className="text-sm text-zinc-400">
              {lang === "en" ? "Manage which reminders we send to your inbox."
                : lang === "fr" ? "Gérez quels rappels nous vous envoyons."
                : lang === "it" ? "Gestisci quali promemoria ti inviamo."
                : "Steuere, welche Erinnerungen wir dir per E-Mail schicken."}
            </p>
          </div>
        </div>
        <div className="mt-6 flex items-start gap-3 rounded-lg border border-zinc-800 bg-black/30 p-4">
          <input
            id="boost-reminder-email-toggle"
            type="checkbox"
            checked={!emailReminderOff}
            disabled={savingNotif}
            onChange={(e) => toggleEmailReminder(!e.target.checked)}
            className="mt-1 h-4 w-4 cursor-pointer accent-red-600"
            data-testid="account-boost-reminder-email-toggle"
          />
          <label htmlFor="boost-reminder-email-toggle" className="flex-1 cursor-pointer select-none">
            <span className="block text-sm font-semibold text-white">
              {lang === "en" ? "Boost-expiring email reminders"
                : lang === "fr" ? "Rappels e-mail d'expiration de boost"
                : lang === "it" ? "Promemoria e-mail di scadenza boost"
                : "Boost-Ablauf-Erinnerungen per E-Mail"}
            </span>
            <span className="mt-0.5 block text-xs leading-relaxed text-zinc-500">
              {lang === "en" ? "Get an email 24-48h before a boost expires, including performance insight. Only sent when push notifications are not available."
                : lang === "fr" ? "Recevez un email 24-48h avant l'expiration d'un boost, avec l'analyse de performance. Envoyé uniquement si les notifications push ne sont pas disponibles."
                : lang === "it" ? "Ricevi un'email 24-48h prima della scadenza del boost, con analisi delle performance. Inviata solo se le notifiche push non sono disponibili."
                : "Erhalte 24-48h vor Boost-Ablauf eine E-Mail mit Performance-Insight. Wird nur versendet, wenn keine Push-Benachrichtigungen verfügbar sind."}
            </span>
          </label>
        </div>
      </Card>

      {/* COMPARE HISTORY */}
      <Card className="mt-6 border-zinc-800 bg-zinc-950 p-6 text-white sm:p-8" data-testid="account-compare-history-card">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-600/15 text-red-500"><GitCompareArrows className="h-4 w-4" /></div>
          <div>
            <h2 className="text-lg font-semibold">Vergleichs-Verlauf</h2>
            <p className="text-sm text-zinc-400">Deine letzten 5 Fahrzeug-Vergleiche.</p>
          </div>
        </div>
        {compareLoading ? (
          <p className="mt-6 text-sm text-zinc-500">Wird geladen…</p>
        ) : compareHistory.length === 0 ? (
          <p className="mt-6 text-sm text-zinc-500" data-testid="account-compare-history-empty">
            Du hast noch keinen Vergleich gespeichert.
          </p>
        ) : (
          <ul className="mt-6 space-y-2">
            {compareHistory.map((entry) => {
              const titles = (entry.titles || []).filter(Boolean);
              const label = titles.length > 0 ? titles.join(" · ") : `${entry.ids.length} Fahrzeuge`;
              const ts = entry.at ? new Date(entry.at) : null;
              const tsLabel = ts ? new Intl.DateTimeFormat("de-CH", { dateStyle: "short", timeStyle: "short" }).format(ts) : "";
              return (
                <li
                  key={entry.id}
                  data-testid={`account-compare-history-item-${entry.id}`}
                  className="group flex items-center gap-3 rounded-lg border border-zinc-800 bg-black p-3 hover:border-red-500/40 sm:p-4"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-zinc-900 text-zinc-500">
                    <History className="h-4 w-4" />
                  </div>
                  <button
                    type="button"
                    onClick={() => reopenCompare(entry)}
                    className="min-w-0 flex-1 text-left"
                    data-testid={`account-compare-reopen-${entry.id}`}
                  >
                    <p className="line-clamp-1 text-sm font-semibold text-white group-hover:text-red-300">{label}</p>
                    <p className="mt-0.5 text-[11px] text-zinc-500">{tsLabel} · {entry.ids.length} Fahrzeuge</p>
                  </button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => reopenCompare(entry)}
                    className="shrink-0 bg-red-600 px-3 text-xs text-white hover:bg-red-500"
                    data-testid={`account-compare-cta-${entry.id}`}
                  >
                    Erneut vergleichen
                  </Button>
                  <button
                    type="button"
                    onClick={() => deleteCompareEntry(entry.id)}
                    title="Entfernen"
                    className="shrink-0 rounded-md p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-red-300"
                    data-testid={`account-compare-remove-${entry.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* SECURITY */}
      <Card className="mt-6 border-zinc-800 bg-zinc-950 p-6 text-white sm:p-8" data-testid="account-security-card">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-600/15 text-red-500"><Lock className="h-4 w-4" /></div>
          <div>
            <h2 className="text-lg font-semibold">{t.account.securityTitle}</h2>
            <p className="text-sm text-zinc-400">{t.account.securitySubtitle}</p>
          </div>
        </div>
        <form onSubmit={submitPwd} className="mt-6 space-y-5">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.account.currentPassword}</Label>
            <Input type="password" required autoComplete="current-password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} className="mt-2 border-zinc-800 bg-black text-white" data-testid="account-current-password" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.account.newPassword}</Label>
            <Input type="password" required minLength={6} autoComplete="new-password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-2 border-zinc-800 bg-black text-white" data-testid="account-new-password" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">{t.account.confirmNew}</Label>
            <Input type="password" required minLength={6} autoComplete="new-password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} className="mt-2 border-zinc-800 bg-black text-white" data-testid="account-confirm-password" />
          </div>
          {pwdError && <p className="text-sm text-red-500" data-testid="account-pwd-error">{pwdError}</p>}
          <Button type="submit" disabled={savingPwd || !curPwd || !newPwd || !confirmPwd} className="w-full bg-red-600 text-white hover:bg-red-500 sm:w-auto" data-testid="account-change-password">
            {savingPwd ? "..." : t.account.changePassword}
          </Button>
        </form>
      </Card>

      {/* DANGER ZONE */}
      <Card className="mt-6 border-red-500/30 bg-red-500/5 p-6 text-white sm:p-8" data-testid="account-danger-card">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-red-600/20 text-red-400"><ShieldAlert className="h-4 w-4" /></div>
          <div>
            <h2 className="text-lg font-semibold text-red-100">{t.account.dangerTitle}</h2>
            <p className="text-sm text-red-200/70">{t.account.dangerSubtitle}</p>
          </div>
        </div>
        {!deleteOpen ? (
          <Button onClick={() => setDeleteOpen(true)} variant="outline" className="mt-5 border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="account-delete-cta">
            {t.account.deleteCta}
          </Button>
        ) : (
          <div className="mt-5 space-y-4 rounded-lg border border-red-500/40 bg-black/40 p-5" data-testid="account-delete-confirm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
              <div>
                <p className="text-sm font-semibold text-red-100">{t.account.deleteConfirmTitle}</p>
                <p className="mt-1 text-sm text-red-200/80">{t.account.deleteConfirmText}</p>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-red-200/80">{t.account.currentPassword}</Label>
              <Input type="password" autoComplete="current-password" value={deletePwd} onChange={(e) => setDeletePwd(e.target.value)} className="mt-2 border-red-500/30 bg-black text-white" data-testid="account-delete-password" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-red-200/80">{t.account.deleteConfirmInput}</Label>
              <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder={t.account.deleteConfirmKeyword} className="mt-2 border-red-500/30 bg-black font-mono text-white" data-testid="account-delete-keyword" />
            </div>
            {deleteError && <p className="text-sm text-red-400" data-testid="account-delete-error">{deleteError}</p>}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={submitDelete} disabled={deleting} className="bg-red-600 text-white hover:bg-red-500" data-testid="account-delete-final">
                {deleting ? "..." : t.account.deleteFinal}
              </Button>
              <Button onClick={() => { setDeleteOpen(false); setDeletePwd(""); setDeleteConfirm(""); setDeleteError(""); }} variant="outline" className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-900" data-testid="account-delete-cancel">
                {t.account.deleteCancel}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
